/**
 * Cart types matching backend API responses
 * @see specs/007-cart-checkout/contracts/cart-api.md
 */

// =============================================================================
// Backend Response Types (snake_case → camelCase transformed)
// =============================================================================

/**
 * Cart status from backend
 */
export type CartStatus = 'active' | 'converted' | 'expired';

/**
 * Product info embedded in cart variant
 */
export interface CartProductInfo {
  id: number;
  name: string;
  slug: string;
  thumbnailUrl?: string;
  // Category slug — drives per-category shipping surcharge thresholds.
  // Optional because older backend responses don't include it; missing
  // means "treat as default" in the surcharge function.
  categorySlug?: string | null;
}

/**
 * Product variant info embedded in cart item
 */
export interface CartVariantInfo {
  id: number;
  sku: string;
  price: number;
  attributes: Record<string, string>;
  inStock: boolean;
  quantity: number; // Available stock
  product: CartProductInfo;
}

/**
 * Cart item from backend API (camelCase)
 */
export interface CartItemResponse {
  id: number;
  variantId: number;
  quantity: number;
  unitPrice: number;
  variant: CartVariantInfo;
}

/**
 * Full cart from backend API (camelCase)
 */
export interface CartResponse {
  id: number | null;
  userId?: number | null;
  sessionId?: string | null;
  status: CartStatus;
  items: CartItemResponse[];
  subtotal: number;
  createdAt: string | null;
  updatedAt: string | null;
}

// =============================================================================
// Backend Request Types
// =============================================================================

/**
 * Request to add item to cart
 */
export interface AddCartItemRequest {
  variantId: number;
  quantity: number;
}

/**
 * Request to update cart item quantity
 */
export interface UpdateCartItemRequest {
  quantity: number;
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Response when cart operation adjusts quantity due to stock
 */
export interface CartOperationResult {
  cart: CartResponse;
  adjusted?: boolean;
  message?: string | null;
}

/**
 * Single stock adjustment detail
 */
export interface StockAdjustment {
  variantId: number;
  requestedQuantity: number;
  availableQuantity: number;
  productName: string;
}

/**
 * Cart validation result from POST /cart/validate
 */
export interface CartValidationResult {
  valid: boolean;
  adjustments: StockAdjustment[];
}

// =============================================================================
// API Error Types
// =============================================================================

/**
 * Cart API error codes
 */
export type CartErrorCode =
  | 'OUT_OF_STOCK'
  | 'INSUFFICIENT_STOCK'
  | 'VARIANT_NOT_FOUND'
  | 'CART_NOT_FOUND'
  | 'CART_ITEM_NOT_FOUND'
  | 'CART_EXPIRED'
  | 'INVALID_QUANTITY'
  | 'UNAUTHORIZED';

/**
 * Cart API error response
 */
export interface CartApiError {
  error: {
    code: CartErrorCode;
    message: string;
  };
}

// =============================================================================
// Frontend Normalized Types
// =============================================================================

/**
 * Frontend cart item (normalized for UI)
 */
export interface CartItem {
  id: number;
  variantId: number;
  productId: number;
  productName: string;
  productSlug: string;
  // Optional — used by the shipping estimate to apply category-specific
  // surcharge rules. May be undefined for older payloads.
  categorySlug?: string | null;
  sku: string;
  price: number;
  quantity: number;
  maxQuantity: number; // Available stock
  attributes: Record<string, string>;
  imageUrl?: string;
  inStock: boolean;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Transform backend cart item to frontend CartItem
 */
export function transformCartItem(item: CartItemResponse): CartItem {
  return {
    id: item.id,
    variantId: item.variantId,
    productId: item.variant.product.id,
    productName: item.variant.product.name,
    productSlug: item.variant.product.slug,
    categorySlug: item.variant.product.categorySlug ?? null,
    sku: item.variant.sku,
    price: item.unitPrice,
    quantity: item.quantity,
    maxQuantity: item.variant.quantity,
    attributes: item.variant.attributes,
    imageUrl: item.variant.product.thumbnailUrl,
    inStock: item.variant.inStock,
  };
}

/**
 * Transform backend cart response to frontend items array
 */
export function transformCartItems(cart: CartResponse): CartItem[] {
  return cart.items.map(transformCartItem);
}

/**
 * Calculate total item count from cart items
 */
export function calculateItemCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}
